import { Alert } from "../../../../../../components/alert/Alert";
import { apiV2AssertError } from "../../../../../../hooks/api/v2/apiV2Request";
import { useProductCatalogueMode } from "../../../../../../hooks/useProductCatalogueMode";
import { Button } from "@sellar-io/components";
import type { SupplierProductGetResponseChildType } from "@sellar-io/api-v2/types";
import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../../../../components/modal/Dialog";
import {
  ProductItemAvailabilitySection,
  type ProductItemAvailabilitySectionHandle,
} from "../../../shared/ProductItemAvailabilitySection";

export const ItemVisibilityDialog = ({
  product,
  active,
  onDismiss,
  afterLeave,
}: {
  product: SupplierProductGetResponseChildType | null;
  active: boolean;
  onDismiss: () => void;
  afterLeave?: () => void;
}) => {
  const productCatalogueMode = useProductCatalogueMode();
  return (
    <Dialog
      active={active && product !== null}
      onDismiss={onDismiss}
      afterLeave={afterLeave}
    >
      <DialogNavigation
        title={product?.name ? `${product.name} visibility` : "Item visibility"}
        description={
          productCatalogueMode.isBasicMode
            ? "Control whether this product item is visible to customers."
            : "Control this product item’s visibility by selecting which product catalogues it appears on"
        }
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
      />
      {product && (
        <ItemVisibilityContent
          key={product.id}
          mode={productCatalogueMode.isBasicMode ? "basic" : "advanced"}
          product={product}
          onDismiss={onDismiss}
        />
      )}
    </Dialog>
  );
};

const ItemVisibilityContent = ({
  mode,
  product,
  onDismiss,
}: {
  mode: "basic" | "advanced";
  product: SupplierProductGetResponseChildType;
  onDismiss: () => void;
}) => {
  const [sessionMode] = useState(mode);
  const availabilityRef = useRef<ProductItemAvailabilitySectionHandle>(null);
  const productVisibilitySaveMutation = useMutation({
    retry: false,
    mutationFn: async () => {
      const availability = availabilityRef.current;
      if (!availability) {
        throw new Error("Visibility settings are not ready. Please try again.");
      }
      await availability.save();
    },
  });
  const handleConfirm = () => {
    productVisibilitySaveMutation.mutate(undefined, { onSuccess: onDismiss });
  };

  return (
    <>
      <DialogContent className={"flex flex-col gap-4 p-4"}>
        {productVisibilitySaveMutation.isError && (
          <Alert
            type="error"
            message={
              apiV2AssertError(productVisibilitySaveMutation.error)?.response
                ?.data.message ?? "Unable to save visibility. Please try again."
            }
          />
        )}
        <ProductItemAvailabilitySection
          ref={availabilityRef}
          mode={sessionMode}
          product={product}
        />
      </DialogContent>
      <DialogCTA>
        <Button
          loading={productVisibilitySaveMutation.isPending}
          onClick={handleConfirm}
        >
          Confirm
        </Button>
      </DialogCTA>
    </>
  );
};
